import React from 'react';
import { Link } from 'react-router-dom';
import { Calendar, Gift, MapPin, Clock, Phone, Mail, CheckCircle } from 'lucide-react';

const NewYearCampaignPage = () => {
  return (
    <div className="bg-white">
      {/* Hero Section */}
      <section 
        className="relative min-h-[500px] flex items-center justify-center"
        style={{
          background: 'linear-gradient(135deg, #FFF7FA 0%, #FFE5EC 50%, #FFFFFF 100%)'
        }}
      >
        <div className="absolute inset-0 opacity-10">
          <img 
            src="https://images.unsplash.com/photo-1546484396-fb3fc6f95f98?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzZ8MHwxfHNlYXJjaHwxfHxuZXclMjB5ZWFyJTIwamFwYW58ZW58MHx8fHwxNzY0NjU1MzQwfDA&ixlib=rb-4.1.0&q=85"
            alt="新年キャンペーン"
            className="w-full h-full object-cover"
          />
        </div>
        
        <div className="relative z-10 container mx-auto px-4 text-center max-w-4xl">
          <div className="fade-in">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6" style={{ color: '#DC143C' }}>
              🌸 新年キャンペーン告知用<br />Webページ制作
            </h1>
            <p className="text-xl md:text-2xl mb-4" style={{ color: '#2C2C2C' }}>
              年末年始の集客を成功させる
            </p>
            <p className="text-lg md:text-xl mb-8 text-gray-700">
              「見た人がそのまま予約・来店・問い合わせできる」<br />
              新年キャンペーン専用のWebページを制作します
            </p>
            <div className="mb-8 max-w-2xl mx-auto">
              <p className="text-base md:text-lg text-gray-600 leading-relaxed">
                本キャンペーンは、<br />
                「今すぐ何かを始めたい」という方向けではなく、<br />
                まずは状況を整理して考えたい方のための取り組みです。
              </p>
              <p className="text-base md:text-lg text-gray-600 leading-relaxed mt-4">
                期間中は、相談しやすい形で<br />
                事業状況の整理・簡易診断をご案内しています。
              </p>
            </div>
            <Link 
              to="/contact"
              className="inline-block bg-white px-8 py-4 rounded-full shadow-lg hover:shadow-xl transition-shadow"
            >
              <p className="text-lg font-semibold" style={{ color: '#DC143C' }}>
                🎍 新年キャンペーン用ページを相談する
              </p>
            </Link>
          </div>
        </div>
        
        {/* Decorative Elements */}
        <div className="absolute top-10 left-10 text-6xl opacity-20">🌸</div>
        <div className="absolute bottom-10 right-10 text-6xl opacity-20">🎍</div>
      </section>

      {/* Problem Section */}
      <section className="section-spacing bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold mb-8 text-center" style={{ color: '#DC143C' }}>
              📌 こんなお悩みはありませんか？
            </h2>
            
            <div className="bg-white rounded-3xl p-8 shadow-lg">
              <ul className="space-y-4">
                {[
                  '新年キャンペーンをどう告知したらいいか分からない',
                  'SNSやチラシだけでは伝えきれない',
                  '忙しくてWebのことまで手が回らない',
                  'できればすぐに公開したい'
                ].map((item, index) => (
                  <li key={index} className="flex items-start text-lg">
                    <CheckCircle className="w-6 h-6 mr-3 mt-1 flex-shrink-0" style={{ color: '#DC143C' }} />
                    <span className="text-gray-700">{item}</span>
                  </li>
                ))}
              </ul>
              
              <div className="mt-8 text-center">
                <p className="text-2xl font-bold" style={{ color: '#DC143C' }}>
                  👉 すべて、こちらで対応します。
                </p>
              </div>
            </div>
            
            <p className="text-sm text-gray-500 text-center mt-6">
              ※ 個人事業主・小規模事業者さま向けのサービスです
            </p>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="section-spacing bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4" style={{ color: '#DC143C' }}>
              🎁 新年キャンペーン用Webページでできること
            </h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            <div className="bg-gradient-to-br from-white to-pink-50 rounded-3xl p-8 shadow-lg border-2" style={{ borderColor: '#F7C9D3' }}>
              <div className="text-5xl mb-4 text-center">📄</div>
              <h3 className="text-xl font-bold mb-3 text-center" style={{ color: '#DC143C' }}>
                ✅ 1ページで完結
              </h3>
              <ul className="text-gray-700 space-y-2 text-sm">
                <li>• 新年キャンペーン内容</li>
                <li>• 期間</li>
                <li>• 特典</li>
                <li>• 店舗情報</li>
                <li>• お問い合わせ・予約導線</li>
              </ul>
              <p className="text-gray-600 text-sm mt-3">を1ページにまとめます。</p>
            </div>

            <div className="bg-gradient-to-br from-white to-pink-50 rounded-3xl p-8 shadow-lg border-2" style={{ borderColor: '#F7C9D3' }}>
              <div className="text-5xl mb-4 text-center">📱</div>
              <h3 className="text-xl font-bold mb-3 text-center" style={{ color: '#DC143C' }}>
                ✅ スマホ対応
              </h3>
              <ul className="text-gray-700 space-y-2">
                <li>• スマートフォンから見やすい</li>
                <li>• そのまま電話・メールが可能</li>
                <li>• 指でタップしやすいボタン</li>
              </ul>
            </div>

            <div className="bg-gradient-to-br from-white to-pink-50 rounded-3xl p-8 shadow-lg border-2" style={{ borderColor: '#F7C9D3' }}>
              <div className="text-5xl mb-4 text-center">⚡</div>
              <h3 className="text-xl font-bold mb-3 text-center" style={{ color: '#DC143C' }}>
                ✅ すぐに使える
              </h3>
              <ul className="text-gray-700 space-y-2">
                <li>• 内容をヒアリング後、短期間で公開</li>
                <li>• 年末・年始の集客に間に合います</li>
              </ul>
            </div>
          </div>
          
          {/* Mid-page CTA */}
          <div className="text-center mt-12">
            <Link 
              to="/contact"
              className="inline-block bg-gradient-to-r from-pink-500 to-red-500 text-white px-10 py-4 rounded-full text-lg font-bold hover:shadow-xl transition-all"
            >
              無料相談はこちら
            </Link>
          </div>
        </div>
      </section>

      {/* Content Includes Section */}
      <section className="section-spacing" style={{ backgroundColor: '#FFF7FA' }}>
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold mb-8 text-center" style={{ color: '#DC143C' }}>
              🎉 ページに含まれる主な内容
            </h2>
            
            <div className="bg-white rounded-3xl p-8 shadow-lg">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {[
                  '新年キャンペーンタイトル',
                  'キャンペーン期間表示',
                  '特典・サービス内容',
                  '店内写真・イメージ画像',
                  '店舗情報（住所・営業時間など）',
                  'Googleマップ表示',
                  'お問い合わせ・予約ボタン',
                  'SNSシェアボタン（オプション）'
                ].map((item, index) => (
                  <div key={index} className="flex items-start">
                    <Gift className="w-5 h-5 mr-3 mt-1 flex-shrink-0" style={{ color: '#DC143C' }} />
                    <span className="text-gray-700">{item}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="section-spacing bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold mb-12 text-center" style={{ color: '#DC143C' }}>
              🛠 制作の流れ
            </h2>
            
            <div className="space-y-6">
              {[
                {
                  step: '①',
                  title: 'お問い合わせ',
                  description: 'フォームまたはメールでご連絡ください。'
                },
                {
                  step: '②',
                  title: '無料ヒアリング',
                  description: 'キャンペーン内容やご希望をお聞きします。'
                },
                {
                  step: '③',
                  title: 'ページ制作',
                  description: 'こちらでページを作成します（専門知識不要）。'
                },
                {
                  step: '④',
                  title: '公開',
                  description: '確認後、すぐに公開できます。'
                }
              ].map((item, index) => (
                <div key={index} className="bg-gradient-to-r from-pink-50 to-white rounded-2xl p-6 shadow-md border-l-4" style={{ borderColor: '#DC143C' }}>
                  <div className="flex items-start">
                    <div className="text-3xl font-bold mr-4" style={{ color: '#DC143C' }}>
                      {item.step}
                    </div>
                    <div>
                      <h3 className="text-xl font-bold mb-2" style={{ color: '#2C2C2C' }}>
                        {item.title}
                      </h3>
                      <p className="text-gray-700">{item.description}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Why Effective Section */}
      <section className="section-spacing" style={{ backgroundColor: '#FFF7FA' }}>
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold mb-8 text-center" style={{ color: '#DC143C' }}>
              🌸 なぜ「新年専用ページ」が効果的なの？
            </h2>
            
            <div className="bg-white rounded-3xl p-8 shadow-lg">
              <div className="space-y-4">
                {[
                  'チラシより詳しく伝えられる',
                  'SNSから直接案内できる',
                  'お客様が迷わず行動できる',
                  '24時間いつでも情報を見てもらえる'
                ].map((item, index) => (
                  <div key={index} className="flex items-start">
                    <CheckCircle className="w-6 h-6 mr-3 mt-1 flex-shrink-0" style={{ color: '#DC143C' }} />
                    <span className="text-lg text-gray-700">{item}</span>
                  </div>
                ))}
              </div>
              
              <div className="mt-8 p-6 bg-pink-50 rounded-xl">
                <p className="text-xl font-bold text-center" style={{ color: '#DC143C' }}>
                  👉 「見ただけで内容が分かる」ことが、来店につながります。
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Limited Time Section */}
      <section className="section-spacing bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold mb-6" style={{ color: '#DC143C' }}>
              📅 期間限定対応について
            </h2>
            
            <div className="bg-gradient-to-br from-pink-50 to-white rounded-3xl p-8 shadow-lg">
              <p className="text-lg mb-6 text-gray-700">
                12月〜1月は、新年キャンペーン準備のご相談が最も多い時期です。
              </p>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                {[
                  '急ぎの制作にも対応',
                  '小規模店舗様も大歓迎'
                ].map((item, index) => (
                  <div key={index} className="flex items-center justify-center">
                    <CheckCircle className="w-5 h-5 mr-2" style={{ color: '#DC143C' }} />
                    <span className="font-semibold text-gray-700">{item}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section 
        className="section-spacing text-center"
        style={{ 
          background: 'linear-gradient(135deg, #DC143C 0%, #FF6B9D 100%)'
        }}
      >
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-4xl md:text-5xl font-bold mb-6 text-white">
              ☎️ まずはお気軽にご相談ください
            </h2>
            <p className="text-xl mb-4 text-white opacity-90">
              強引な営業は一切ありません。
            </p>
            <p className="text-lg mb-8 text-white opacity-90">
              「こんなことできる？」というご質問だけでも大丈夫です。
            </p>
            
            <Link 
              to="/contact"
              className="inline-block bg-white px-10 py-4 rounded-full text-lg font-bold hover:bg-gray-100 transition-colors"
              style={{ color: '#DC143C' }}
            >
              無料相談はこちら
            </Link>
          </div>
        </div>
      </section>

      {/* Reassurance Section */}
      <section className="section-spacing bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold mb-8 text-center" style={{ color: '#DC143C' }}>
              📝 補足（安心ポイント）
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {[
                {
                  icon: '💻',
                  title: 'Webが苦手でも大丈夫',
                  description: '難しいことは全部こちらで対応'
                },
                {
                  icon: '💬',
                  title: '専門用語は使いません',
                  description: 'わかりやすい言葉でご説明'
                },
                {
                  icon: '🤝',
                  title: '修正・相談も丁寧に対応',
                  description: '納得いくまでサポート'
                }
              ].map((item, index) => (
                <div key={index} className="bg-white rounded-2xl p-6 shadow-md text-center">
                  <div className="text-4xl mb-3">{item.icon}</div>
                  <h3 className="text-lg font-bold mb-2" style={{ color: '#2C2C2C' }}>
                    {item.title}
                  </h3>
                  <p className="text-sm text-gray-600">{item.description}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Footer Note */}
      <section className="py-12 bg-white border-t border-gray-200">
        <div className="container mx-auto px-4 text-center">
          <p className="text-sm text-gray-600 mb-2">
            このページは新年キャンペーン告知用のWebページのご案内です。
          </p>
          <p className="text-sm text-gray-600 mb-2">
            内容は店舗様ごとにカスタマイズできます。
          </p>
          <p className="text-xs text-gray-500 mt-4">
            ※ 本ページは期間限定キャンペーンです。（終了後は通常サービスへご案内します）
          </p>
          
          <div className="mt-8">
            <Link 
              to="/"
              className="text-[#F7C9D3] hover:text-[#DC143C] font-medium"
            >
              ← トップページに戻る
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default NewYearCampaignPage;
